import React, {useEffect, useState} from 'react';
import TopTitleBlock from '../../Components/TopTitleBlock/TopTitleBlock';
import s from './ListOfInvoices.module.css';
import LightGreenButton from '../../Components/Buttons/LightGreenButton';
import FunctionalButtonPanel, {
	FunctionalButton,
} from '../../Components/FunctionalButtonPanel/FunctionalButtonPanel';
import AddFilterButton from '../../Components/Buttons/AddFilterButton';
import GridTableHead from '../../Components/GridTableHead/GridTableHead';
import TableTotalBlock from '../../Components/TableCommonDirectory/TableElements/TableTotalBlock';
import InvoiceTableRowEl from '../../Components/TableCommonDirectory/TableElements/InvoiceTableRowEl';
import HistoryOfChangesInvoice from './HistoryOfChangesInvoice';
import ListInvoicesFilter from '../../Components/Filter/ListInvoicesFilter';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import TopPageInfoBlock from '../../Components/TopPageInfoBlock/TopPageInfoBlock';
import {OrdersType} from '../../types/TypesStore';
import {useTranslation} from 'react-i18next';
import PreLoaderComponent from '../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import {
	useGetOrdersHistoryQuery,
	useGetOrdersListQuery,
} from '../../redux/DepotSlice/depotOrdersApi';
import useClickMoreButton from '../../hooks/useClickMoreButton';
import {useStatusRestriction} from '../../hooks/useStatusRestriction';
import StatusRestrictionMessage from '../../Components/StatusRestrictionMessage/StatusRestrictionMessage';

const ListOfInvoices = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const {t} = useTranslation();
	const restrictions = useStatusRestriction();
	const [filterMenu, setFilterMenu] = useState(false);

	const defaultFilterValues = {
		filter_active: false,
		offset: 0,
		status: undefined,
		delivery_type: undefined,
		date_from: undefined,
		date_to: undefined,
	};

	const [filters, setFilters] = useState<any>(defaultFilterValues);

	const {handleClickMoreButton} = useClickMoreButton(setFilters);

	const {data: ordersData, isLoading: isLoadingOrders} = useGetOrdersListQuery(
		filters,
		{
			refetchOnMountOrArgChange: true,
			skip: !restrictions.canAccessWarehouseSection,
		},
	);
	const {data: historyData, isLoading: isLoadingHistory} =
		useGetOrdersHistoryQuery(filters, {
			refetchOnMountOrArgChange: true,
			skip: !restrictions.canAccessWarehouseSection,
		});

	if (!restrictions.canAccessWarehouseSection) {
		return (
			<div className="page__content">
				<StatusRestrictionMessage message={t('Сторінка недоступна')} />
			</div>
		);
	}

	const handleClickFilterButton = (value: string) => {
		const status = value.length > 0 ? value : undefined;
		setFilters({
			filter_active: 1,
			status,
			offset: 0,
			delivery_type: undefined,
			date_from: undefined,
			date_to: undefined,
		});
	};

	const functionalityButtons: FunctionalButton[] = [
		{
			name: t('Нові '),
			value: 'ordered',
			active: filters.status === 'ordered',
		},
		{
			name: t('Відправлені'),
			value: 'shipped',
			active: filters.status === 'shipped',
		},
		{
			name: t('Отримано'),
			value: 'receive',
			active: filters.status === 'receive',
		},
		{
			name: t('Підтверджено'),
			value: 'receive_outcome',
			active: filters.status === 'receive_outcome',
		},
		{name: t('Всі'), value: ''},
	];

	useEffect(() => {
		if (location.state?.defaultFilters) {
			setFilters({
				...defaultFilterValues,
				...location.state.defaultFilters,
			});
			// чистим state после первого рендера
			setTimeout(() => {
				navigate(location.pathname, {replace: true});
			}, 0);
		} else {
			setFilters(defaultFilterValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="page__content">
			<TopTitleBlock title={t('список накладних')} />
			<TopPageInfoBlock
				text={t(
					'Тут перелік ваших замовлень. За замовчуванням відображаються всі замовлення за останній місяць. Щоб побачити ті, які вам потрібно підтвердити (Надіслані) або ті, які ви вже підтвердили (Підтверджено) або інше, то ви можете натиснути кнопки нижче.',
				)}
				subText={t(
					'Також нижче є можливість знайти замовлення за номером, за продуктом у накладній та за статусом.',
				)}
			/>
			<div className={s.greenButtonWrapper}>
				<Link to={'/create_invoice'}>
					<LightGreenButton type={'button'} text={t('створити накладну')} />
				</Link>
			</div>
			<div className={s.buttonsPanelWrapper}>
				<FunctionalButtonPanel
					functionality={functionalityButtons}
					elWidth={'1 1 15%'}
					callback={handleClickFilterButton}
				/>
			</div>
			<AddFilterButton
				filterMenu={filterMenu}
				setFilterMenu={setFilterMenu}
				color={'blue'}
			/>
			<ListInvoicesFilter
				active={filterMenu}
				setActive={setFilterMenu}
				setFilters={setFilters}
			/>
		{isLoadingOrders ? (
			<PreLoaderComponent type={'category'} size={'medium'} />
		) : (
			<div className={s.tableScrollWrapper}>
				<div className={s.tableContainer}>
					<GridTableHead
						columns={[
							'№',
							'Дата створення',
							'Тип доставки',
							'Статус',
							'ТТН',
							'Дії',
						]}
						gridTemplateColumns="2fr 2fr 2fr 2fr 2fr 2fr"
					/>
					{ordersData &&
						ordersData.orders?.map((order: OrdersType, index: number) => (
							<InvoiceTableRowEl key={order.id} data={order} index={index} />
						))}
					{(() => {
						const length = ordersData?.orders?.length || 0;
						return ordersData && ordersData.orders && length % 10 === 0;
					})() && (
						<div className="shopping-button">
							<LightGreenButton
								type={'button'}
								text={t('дивитися більше')}
								onClick={handleClickMoreButton}
							/>
						</div>
					)}
					<TableTotalBlock
						info={[
							{
								title: t('Кількість'),
								data: ordersData?.count_orders || 0,
								currency: '',
							},
							{
								title: t('Всього балів'),
								data: ordersData?.sum_points || 0,
								currency: ' ' + t('Б'),
							},
							{
								title: t('Всього грошей'),
								data: ordersData?.sum_money || 0,
								currency: ' ГРН',
							},
						]}
					/>
				</div>
			</div>
		)}
			{isLoadingHistory ? (
				<PreLoaderComponent type={'category'} size={'medium'} />
			) : historyData?.actions && historyData.actions?.length > 0 ? (
				<HistoryOfChangesInvoice ordersHistory={historyData.actions} />
			) : (
				<div className={s.historyOfChangeContainer}>
					<div className={s.historyTitle}>{t('Історія змін накладних')}</div>
					<div className={s.historyEmptyMessage}>
						{t('Історія змін для накладних відсутня')}
					</div>
				</div>
			)}
		</div>
	);
};

export default ListOfInvoices;
